import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    CreateCustomAttributeDefinitionRequest,
    CustomAttributeDefinition,
    FieldTypesResponse,
    ReorderCustomAttributeDefinitionsRequest,
    UpdateCustomAttributeDefinitionRequest,
} from '@/models/customAttribute';
import httpClient, { ApiResponse } from '@/api/httpClient.ts';

// Query keys
const QUERY_KEYS = {
    definitions: ['custom-attribute-definitions'] as const,
    definition: (id: string) => ['custom-attribute-definition', id] as const,
    values: (entityType: string, entityId: string) =>
        ['custom-attribute-values', entityType, entityId] as const,
    fieldTypes: ['custom-attribute-field-types'] as const,
};

// Custom Attribute Definitions Hooks
export const useCustomAttributeDefinitions = () => {
    return useQuery({
        queryKey: QUERY_KEYS.definitions,
        queryFn: async (): Promise<CustomAttributeDefinition[]> => {
            const { data } = await httpClient.get<ApiResponse<CustomAttributeDefinition[]>>(
                '/crm/system/custom-attribute-definitions'
            );
            return data.data;
        },
    });
};

export const useCustomAttributeDefinition = (id: string) => {
    return useQuery({
        queryKey: QUERY_KEYS.definition(id),
        queryFn: async (): Promise<CustomAttributeDefinition> => {
            const response = await httpClient.get(`/crm/system/custom-attribute-definitions/${id}`);
            return response.data.definition;
        },
        enabled: !!id,
    });
};

export const useCreateCustomAttributeDefinition = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (
            data: CreateCustomAttributeDefinitionRequest
        ): Promise<CustomAttributeDefinition> => {
            const response = await httpClient.post(
                '/crm/system/custom-attribute-definitions',
                data
            );
            return response.data.definition;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.definitions });
        },
    });
};

export const useUpdateCustomAttributeDefinition = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            id,
            data,
        }: {
            id: string;
            data: UpdateCustomAttributeDefinitionRequest;
        }): Promise<CustomAttributeDefinition> => {
            const response = await httpClient.put(
                `/crm/system/custom-attribute-definitions/${id}`,
                data
            );
            return response.data.definition;
        },
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.definitions });
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.definition(id) });
        },
    });
};

export const useDeleteCustomAttributeDefinition = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string): Promise<void> => {
            await httpClient.delete(`/crm/system/custom-attribute-definitions/${id}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.definitions });
        },
    });
};

export const useReorderCustomAttributeDefinitions = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: ReorderCustomAttributeDefinitionsRequest): Promise<void> => {
            await httpClient.post('/crm/system/custom-attribute-definitions/reorder', data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.definitions });
        },
    });
};

export const useCustomAttributeFieldTypes = () => {
    return useQuery({
        queryKey: QUERY_KEYS.fieldTypes,
        queryFn: async (): Promise<FieldTypesResponse> => {
            const response = await httpClient.get<FieldTypesResponse>(
                '/crm/system/custom-attribute-definitions/field-types'
            );
            return response.data;
        },
    });
};

// Combined hooks for easier usage
export const useCustomAttributeDefinitionsManagement = () => {
    const definitions = useCustomAttributeDefinitions();
    const createDefinition = useCreateCustomAttributeDefinition();
    const updateDefinition = useUpdateCustomAttributeDefinition();
    const deleteDefinition = useDeleteCustomAttributeDefinition();
    const reorderDefinitions = useReorderCustomAttributeDefinitions();
    const fieldTypes = useCustomAttributeFieldTypes();

    return {
        definitions: definitions.data || [],
        isLoading: definitions.isLoading,
        error: definitions.error?.message || null,
        refetch: definitions.refetch,
        createDefinition: createDefinition.mutateAsync,
        updateDefinition: updateDefinition.mutateAsync,
        deleteDefinition: deleteDefinition.mutateAsync,
        reorderDefinitions: reorderDefinitions.mutateAsync,
        fieldTypes: fieldTypes.data?.field_types || [],
        isFieldTypesLoading: fieldTypes.isLoading,
    };
};
